import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GroupModal from './GroupModal';
import DeletionReasonModal from './DeletionReasonModal';
import {
  FaLayerGroup,
  FaPlus,
  FaEdit,
  FaTrash,
  FaUsers,
  FaMapMarkerAlt,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

const GroupsView = ({ userInfo, API_BASE_URL, auth }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedGroupForDeletion, setSelectedGroupForDeletion] = useState(null);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState({
    show: false,
    type: '', // 'success' or 'error'
    title: '',
    message: ''
  });
  const [itemsPerPage] = useState(10);
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen width for responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);

      // Validate zone assignment
      if (!userInfo.zone_id) {
        console.warn('No zone assigned to field officer');
        setGroups([]);
        setLoading(false);
        return;
      }

      console.log('Fetching groups for zone:', userInfo.zone_id, 'MMDA:', userInfo.mmda_id);

      const response = await axios.get(
        `${API_BASE_URL}/zonal-groups?userRole=Revenue Officer&mmda_id=${userInfo.mmda_id}&zone_id=${userInfo.zone_id}`,
        auth
      );

      // If no zone filtering on backend, filter on frontend
      let filteredGroups = response.data;
      if (userInfo.zone_id) {
        filteredGroups = response.data.filter(group =>
          group.zone_id === userInfo.zone_id || group.zone_id === parseInt(userInfo.zone_id)
        );
      }

      setGroups(filteredGroups);
    } catch (error) {
      console.error('Error fetching groups:', error);
      // Try fallback approach
      try {
        const fallbackResponse = await axios.get(`${API_BASE_URL}/zonal-groups`, auth);
        const filteredGroups = fallbackResponse.data.filter(group =>
          group.zone_id === userInfo.zone_id || group.zone_id === parseInt(userInfo.zone_id)
        );
        setGroups(filteredGroups);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
        setGroups([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClose = () => {
    setNotification({
      show: false,
      type: '',
      title: '',
      message: ''
    });
  };

  const requestGroupDeletion = async (group) => {
    setSelectedGroupForDeletion(group);
    setShowDeletionModal(true);
  };

  const handleDeletionSubmit = async (reason) => {
    if (!selectedGroupForDeletion || !reason.trim()) return;

    try {
      // Create support ticket for deletion request
      const ticketData = {
        title: `Delete Request - Group: ${selectedGroupForDeletion.group_name}`,
        description: `Field Officer ${userInfo.full_name} is requesting deletion of group:\n\nGroup Details:\n- Group Name: ${selectedGroupForDeletion.group_name}\n- Description: ${selectedGroupForDeletion.description || 'N/A'}\n- Zone: ${userInfo.zone_name || 'N/A'}\n- Status: ${selectedGroupForDeletion.status || 'N/A'}\n\nReason for Deletion:\n${reason}\n\nGroup ID: ${selectedGroupForDeletion.group_id}`,
        priority: 'medium',
        category: 'data_correction',
        created_by: userInfo.user_id
      };

      await axios.post(
        `${API_BASE_URL}/support-tickets`,
        ticketData,
        auth
      );

      // Show success notification
      setNotification({
        show: true,
        type: 'success',
        title: 'Deletion Request Submitted',
        message: `Deletion request for group "${selectedGroupForDeletion.group_name}" has been submitted as a support ticket to your MMDA. It will be reviewed by the back office.`
      });

      // Close modal and reset
      setShowDeletionModal(false);
      setSelectedGroupForDeletion(null);
    } catch (error) {
      console.error('Error submitting deletion request:', error);

      // Show error notification
      setNotification({
        show: true,
        type: 'error',
        title: 'Failed to Submit Deletion Request',
        message: error.response?.data?.error || 'Failed to submit deletion request. Please try again.'
      });
    }
  };

  const handleAddGroup = () => {
    setSelectedGroup(null);
    setIsModalOpen(true);
  };

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setIsModalOpen(true);
  };

  const handleModalSave = () => {
    fetchGroups(); // Refresh the list
    // Don't close modal immediately - let notification show first
    setTimeout(() => {
      setIsModalOpen(false);
      setSelectedGroup(null);
    }, 100);
  };

  const handleModalNotification = (notificationData) => {
    setNotification(notificationData);
    // If it's a success notification, close the modal after a delay
    if (notificationData.type === 'success') {
      setTimeout(() => {
        setIsModalOpen(false);
        setSelectedGroup(null);
      }, 2000); // Give user time to see the success message
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentGroups = groups.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(groups.length / itemsPerPage);

  const GroupCard = ({ group }) => (
    <div style={{
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '0.5rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      border: '1px solid #e9ecef'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ 
            margin: '0 0 0.5rem 0', 
            color: '#2c3e50',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <FaLayerGroup color="#9b59b6" />
            {group.group_name}
          </h3>
          
          {group.description && (
            <p style={{ 
              margin: '0 0 1rem 0', 
              color: '#7f8c8d',
              fontSize: '0.9rem'
            }}>
              {group.description}
            </p>
          )}

          <div style={{ marginBottom: '0.5rem' }}>
            <span style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              color: '#7f8c8d',
              fontSize: '0.9rem'
            }}>
              <FaMapMarkerAlt size={14} />
              {group.zone_name}
            </span>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <span style={{
              padding: '0.25rem 0.5rem',
              borderRadius: '0.25rem',
              fontSize: '0.75rem',
              fontWeight: '500',
              backgroundColor: group.status === 'active' ? '#d4edda' : '#f8d7da',
              color: group.status === 'active' ? '#155724' : '#721c24'
            }}>
              {group.status === 'active' ? 'Active' : 'Inactive'}
            </span>
            <span style={{
              marginLeft: '0.5rem',
              fontSize: '0.75rem',
              color: '#95a5a6'
            }}>
              ID: {group.group_id}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button
            onClick={() => handleEditGroup(group)}
            style={{
              padding: '0.5rem',
              backgroundColor: '#f39c12',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Edit Group"
          >
            <FaEdit size={14} />
          </button>

          <button
            onClick={() => requestGroupDeletion(group)}
            style={{
              padding: '0.5rem',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Request Deletion"
          >
            <FaTrash size={14} />
          </button>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading groups...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '1rem' : '0',
        marginBottom: '2rem'
      }}>
        <h1 style={{ margin: 0, color: '#2c3e50', textAlign: isMobile ? 'center' : 'left' }}>
          Trader Groups in Your Zone
        </h1>
        <button
          onClick={handleAddGroup}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#9b59b6',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.9rem',
            fontWeight: '500',
            alignSelf: isMobile ? 'stretch' : 'auto',
            justifyContent: 'center'
          }}
        >
          <FaPlus />
          Add New Group
        </button>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(auto-fit, minmax(150px, 1fr))' : 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#9b59b6' }}>
            {groups.length}
          </h3>
          <p style={{ margin: 0, color: '#7f8c8d' }}>Total Groups</p>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#27ae60' }}>
            {groups.filter(g => g.status === 'active').length}
          </h3>
          <p style={{ margin: 0, color: '#7f8c8d' }}>Active Groups</p>
        </div>
      </div>

      {/* Groups Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {currentGroups.map((group) => (
          <GroupCard key={group.group_id} group={group} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '0.5rem',
          marginBottom: '2rem'
        }}>
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: currentPage === 1 ? '#95a5a6' : '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <FaChevronLeft />
            Previous
          </button>

          <span style={{
            padding: '0 1rem',
            color: '#7f8c8d',
            fontSize: '0.9rem',
            fontWeight: '500'
          }}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: currentPage === totalPages ? '#95a5a6' : '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Next
            <FaChevronRight />
          </button>
        </div>
      )}

      {groups.length === 0 && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#7f8c8d'
        }}>
          <FaLayerGroup size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <h3>No groups found</h3>
          <p>You don't have any trader groups in your zone yet.</p>
          <button
            onClick={handleAddGroup}
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1.5rem',
              backgroundColor: '#9b59b6',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <FaPlus />
            Create Your First Group
          </button>
        </div>
      )}

      {/* Add/Edit Group Modal */}
      <GroupModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedGroup(null);
        }}
        group={selectedGroup}
        userInfo={userInfo}
        API_BASE_URL={API_BASE_URL}
        auth={auth}
        onSave={handleModalSave}
        onNotification={handleModalNotification}
      />

      {/* Notification Modal */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            textAlign: 'center'
          }}>
            {/* Icon */}
            <div style={{
              marginBottom: '1rem',
              fontSize: '3rem'
            }}>
              {notification.type === 'success' ? '✅' : '❌'}
            </div>

            {/* Title */}
            <h3 style={{
              margin: '0 0 1rem 0',
              color: notification.type === 'success' ? '#27ae60' : '#e74c3c',
              fontSize: '1.25rem',
              fontWeight: 'bold'
            }}>
              {notification.title}
            </h3>

            {/* Message */}
            <p style={{
              margin: '0 0 2rem 0',
              color: '#666',
              fontSize: '1rem',
              lineHeight: '1.5'
            }}>
              {notification.message}
            </p>

            {/* Close Button */}
            <button
              onClick={handleNotificationClose}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: notification.type === 'success' ? '#27ae60' : '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                minWidth: '100px'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Deletion Reason Modal */}
      <DeletionReasonModal
        isOpen={showDeletionModal}
        onClose={() => {
          setShowDeletionModal(false);
          setSelectedGroupForDeletion(null);
        }}
        onSubmit={handleDeletionSubmit}
        itemType="group"
        itemName={selectedGroupForDeletion ? selectedGroupForDeletion.group_name : ''}
        loading={false}
      />
    </div>
  );
};

export default GroupsView;
